import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import type { LoginResult } from "@hik/contracts";
import { ApiError, post, takeUnauthReason } from "../../lib/api";
import { Button } from "../../ui/Button";
import { ErrorSummary, TextField, useFieldError } from "../../ui/Field";
import { AuthLayout } from "./AuthLayout";
import { useT } from "../../lib/i18n";
import { safeNext } from "./next";

export function LoginPage() {
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const expired = params.get("neden") === "sure";
  // Oturum açıkken şirket askıya alındıysa (sunucu 401 tenant_suspended) gerekçesiz açıklama.
  const [lastReason] = useState(() => takeUnauthReason());
  const suspended = params.get("neden") === "askida" || lastReason === "tenant_suspended";
  const t = useT();
  const fe = useFieldError();

  async function submit(e: FormEvent) {
    e.preventDefault();
    // Boş alan sunucuya gitmeden alanın altında söylenir.
    const missing: Record<string, string> = {};
    if (!login.trim()) missing.login = "required";
    if (!password) missing.password = "required";
    if (Object.keys(missing).length) {
      setError(new ApiError(422, "validation_failed", "Bazı alanları kontrol etmeniz gerekiyor.", missing));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const r = await post<{ session: LoginResult["session"] }>("/auth/login", { login, password, client: "web" });
      qc.setQueryData(["session"], r.session);
      navigate(safeNext(params), { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err : new ApiError(0, "internal_error", "Beklenmeyen bir sorun oluştu."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout
      title="Giriş yapın"
      lead="E-posta adresiniz ya da cep telefonu numaranızla."
      footer={
        <>
          <p>
            İK'dan davet kodu mu aldınız? <Link to="/davet">Kodla hesap oluşturun</Link>
          </p>
          <p>
            Şirketinizi ilk kez mi ekliyorsunuz? <Link to="/kurulum">Şirket hesabı açın</Link>
          </p>
        </>
      }
    >
      {expired && !error && <p className="notice notice--info">Oturumunuzun süresi doldu. Güvenliğiniz için yeniden giriş yapın.</p>}
      {suspended && !error && (
        <p className="notice notice--attention" role="status">
          {t("platform.suspendedLogin")}
        </p>
      )}
      <form onSubmit={submit} noValidate className="form">
        <ErrorSummary message={error?.message} fields={error?.fields} />
        <TextField
          label="E-posta veya cep telefonu"
          name="login"
          autoComplete="username"
          inputMode="email"
          value={login}
          onChange={(e) => setLogin(e.target.value)}
          required
          hint="Örnek: ad@sirket.com ya da 0532 123 45 67"
          error={fe(error?.fields.login)}
        />
        <TextField label="Parola" name="password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} error={fe(error?.fields.password)} required />
        <Button type="submit" variant="primary" size="lg" busy={busy} className="btn--block">
          Giriş yap
        </Button>
        <p className="form__aside">
          <Link to="/parola-sifirla">Parolamı unuttum</Link>
        </p>
      </form>
    </AuthLayout>
  );
}
